export const getNameInitials = (name?: string): string | undefined => {
  if (name) {
    const cleanedName = name
      .replace(/[^a-zA-ZÀ-ÖØ-öø-ÿ\s]+|(?<temp1> )+/g, (_, space) => (space ? " " : ""))
      .trim();
    const splittedName = cleanedName.split(" ");

    if (splittedName.length > 1) {
      return `${splittedName[0][0]}${splittedName[1][0]}`.toUpperCase();
    }

    return `${splittedName[0][0]}${splittedName[0][1]}`.toUpperCase();
  }

  return undefined;
};

export const formatDateOfBirth = (dateOfBirth: string): string => {
  if (dateOfBirth) {
    return dateOfBirth.split("-").reverse().join("/");
  }

  return "-";
};

export const normalizeToHTMLAttribute = (value: string): string => {
  return value.replaceAll(" ", "-");
};

/**
 * Calcula a duração de um agendamento e retorna formatado
 *
 * @param start - hora inicio no formato HH:MM
 * @param end - hora final no formato HH:MM
 * @returns duração no formato 1 h 40 min, 50 min ou 1 h
 */
export const calculateFormattedDuration = (start: string, end: string): string => {
  const [startHours, startMinutes] = start.split(":");
  const [endHours, endMinutes] = end.split(":");
  const diff =
    new Date(0, 0, 0, Number(endHours), Number(endMinutes), 0).getTime() -
    new Date(0, 0, 0, Number(startHours), Number(startMinutes), 0).getTime();
  const hours = Math.floor(diff / 1000 / 60 / 60);
  const minutes = Math.floor((diff - hours * 1000 * 60 * 60) / 1000 / 60);

  const formattedHours = hours > 0 ? `${hours} h` : "";
  const formattedMinutes = minutes > 0 ? `${minutes} min` : "";
  return `${formattedHours} ${formattedMinutes}`.trim();
};

/**
 * Recebe um documento (CPF ou CNPJ) e retorna formatado.
 *
 * @param type - Tipo do documento
 * @param document - O documento a ser formatado. Ex: 49205263001 ou 92282579000140
 * @returns O documento formatado. Ex: 492.052.630-01 ou 92.282.579/0001-40.
 */
export const formatRecipientDocument = (type: string, document: string): string => {
  if (type === "company") {
    // eslint-disable-next-line prefer-named-capture-group -- Não há a necessidade de explicar os grupos.
    const cnpjRegex = /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/;
    return document.replace(cnpjRegex, "$1.$2.$3/$4-$5");
  }
  // eslint-disable-next-line prefer-named-capture-group -- Não há a necessidade de explicar os grupos.
  const cpfRegex = /^(\d{3})(\d{3})(\d{3})(\d{2})/;
  return document.replace(cpfRegex, "$1.$2.$3-$4");
};

export const formatFirstLetterUppercase = (text: string): string => {
  if (!text) {
    return "";
  }

  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Verifica se o nome possui pelo menos duas partes (nome + sobrenome) e caracteres válidos.
 * Retorna true para nomes como "João Silva"; false para "João".
 */
export const isFullName = (name: string): boolean => {
  if (!name || name.length > 120) {
    return false;
  }

  return /^[A-Za-záàâãéèêíïóôõöúçñ-]+\s[A-Za-záàâãéèêíïóôõöúçñ' -]+$/.test(
    name.trim().toLocaleLowerCase(),
  );
};

export const formatCEP = (cep: string): string => {
  const cleaned = cep.replace(/\D/g, "");
  return cleaned.replace(/(?<prefix>\d{5})(?<suffix>\d{3})/, "$<prefix>-$<suffix>");
};

export const formatRG = (rg: string): string => {
  const cleaned = rg.replace(/[^\dA-Za-z]/g, "");

  const digits = cleaned.replace(/\D/g, "");
  const lastChar = cleaned.slice(-1);
  const hasLetterSuffix = /[A-Za-z]/.test(lastChar);

  // RG com 9 dígitos (padrão mais comum) - XX.XXX.XXX-X
  if (digits.length === 9 && !hasLetterSuffix) {
    return digits.replace(
      /(?<firstTwo>\d{2})(?<firstThree>\d{3})(?<secondThree>\d{3})(?<verifier>\d{1})/,
      "$<firstTwo>.$<firstThree>.$<secondThree>-$<verifier>",
    );
  }

  // RG com 8 dígitos + letra (ex: SP) - XX.XXX.XXX-X
  if (digits.length === 8 && hasLetterSuffix) {
    const formatted = digits.replace(
      /(?<firstTwo>\d{2})(?<firstThree>\d{3})(?<secondThree>\d{3})/,
      "$<firstTwo>.$<firstThree>.$<secondThree>",
    );
    return `${formatted}-${lastChar.toUpperCase()}`;
  }

  // RG com 8 dígitos numéricos (alguns estados) - XX.XXX.XXX
  if (digits.length === 8 && !hasLetterSuffix) {
    return digits.replace(
      /(?<firstTwo>\d{2})(?<firstThree>\d{3})(?<secondThree>\d{3})/,
      "$<firstTwo>.$<firstThree>.$<secondThree>",
    );
  }

  // RG com 7 dígitos (antigos) - X.XXX.XXX
  if (digits.length === 7) {
    return digits.replace(
      /(?<firstOne>\d{1})(?<firstThree>\d{3})(?<secondThree>\d{3})/,
      "$<firstOne>.$<firstThree>.$<secondThree>",
    );
  }

  // Se não se encaixar, retorna apenas os caracteres válidos
  return cleaned;
};
